import moment from 'moment';
import sortBy from 'lodash/sortBy';
import DefaultDialogController from 'utils/notifications/default-dialog-controller';
import { isStoreProfileSelector, isChainHeadSelector, isRetailerDashboardSelector } from 'selectors';
import * as api from 'dashboard/resources/alerts.api';
import alertTemplate from './alert.html';
import alertModalTemplate from './alert-modal.html';
import dashboardHelpers from '../../../../dashboard-helpers';

const mapStateToThisModal = ({ session, dictionary }) => {
  const isChainHead = isChainHeadSelector({ session });
  const { locale, retailerDashboard, loginAs } = session;
  const terms = dictionary.terms[locale];
  const setRead = !retailerDashboard && !loginAs;

  return {
    locale,
    isChainHead,
    terms,
    setRead,
  };
};

const mapDispatchToThisModal = {};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', 'gettextCatalog')
class AlertModalController extends DefaultDialogController {
  constructor() {
    super();
    this.unsubscribe = this.$ngRedux.connect(mapStateToThisModal, mapDispatchToThisModal)(this);
  }

  $onDestroy() {
    this.unsubscribe();
  }

  showRetailerInfo(index) {
    if (this.isChainHead) {
      return this.isFirstAlert(index) || this.isNewAlertRetailer(index);
    }
    return false;
  }

  isNewAlertRetailer(index) {
    return this.isNewAlert(index) && this.isNewAlertDifferent(index, 'retailerId');
  }

  isNewAlertType(index) {
    return this.isNewAlert(index) && this.isNewAlertDifferent(index, 'type');
  }

  isNewAlertOrder(index) {
    return this.isNewAlert(index) && this.isNewAlertDifferent(index, 'orderId');
  }

  isNewAlert(index) {
    return this.content.gridContent[index] && this.content.gridContent[index - 1];
  }

  isNewAlertDifferent(index, property) {
    return this.content.gridContent[index][property] !== this.content.gridContent[index - 1][property];
  }

  isFirstAlert(index) {
    return index === 0;
  }

  isAlertForRetailer(index, retailerId) {
    return this.content.gridContent[index].retailerId === retailerId;
  }

  calculateOrderNumber(type, orderId) {
    return this.content.gridContent.reduce((previousValue, currentAlert) => {
      if (currentAlert.type === type && currentAlert.orderId === orderId) {
        return previousValue + 1;
      }
      return previousValue;
    }, 0);
  }

  calculateOrderValue(type, orderId) {
    return this.content.gridContent.reduce((previousValue, currentAlert) => {
      if (currentAlert.type === type && currentAlert.orderId === orderId) {
        return previousValue + currentAlert.gamePacksValue;
      }
      return previousValue;
    }, 0);
  }
}

const mapStateToThis = ({ session, retailerDashboards, config }) => {
  const isStoreProfile = isStoreProfileSelector({ session });
  const isRetailerDashboard = isRetailerDashboardSelector({ retailerDashboards });
  const { locale, retailer } = session;

  return {
    locale,
    session,
    retailer,
    isStoreProfile,
    isRetailerDashboard,
    config,
  };
};

const mapDispatchToThis = {};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', 'gettextCatalog', '$state', '$element', 'Notifications')
class AlertController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  $onInit() {
    this.retailerId = this.$state.params.retailerNo || this.retailer.retailerId;
    this.widgetClass = `importance-${this.mapPriority(this.data.priority)}`;
    this.$element.addClass(this.widgetClass);
    this.currentDate = moment.unix(this.config.serverTime).format('M/d');
  }

  $onDestroy() {
    this.unsubscribe();
  }

  mapPriority(priority) {
    switch (priority) {
      case 1:
        return 'urgent';
      case 2:
        return 'high';
      default:
        return 'normal';
    }
  }

  openDialog() {
    api.fetchAlertDetails(this.retailerId, this.data.alertId, this.locale, this.setRead).then((results) => {
      if (results[0].type !== 'ArwExpiringGamesAlert') {
        results = this.groupByPacks(results);
      }

      this.data.read = true;
      this.data.type = results ? results[0].type : null;
      this.fillAlertContent(results);

      this.modal = this.Notifications.showComplexDialog({
        controller: AlertModalController,
        template: alertModalTemplate,
        resolve: {
          file: () => this.content.details.file,
          content: () => this.content,
          isStoreProfile: () => this.isStoreProfile,
          showGameInventoryButtons: () => this.showGameInventoryButtons,
          downloadFile: () => this.onDownloadAlertReport.bind(this),
          dismissAlert: () => this.onDismissAlert.bind(this),
          triggerDownload: () => this.triggerDownload.bind(this),
        },
      });
    });
  }

  groupByPacks(results) {
    return sortBy(results, ['type', 'retailerId', 'orderId', 'gameNo', 'packNo']).reduce((previousValue, currentValue) => {
      const lastElement = previousValue[previousValue.length - 1];

      if (
        lastElement &&
        lastElement.type === currentValue.type &&
        lastElement.retailerId === currentValue.retailerId &&
        lastElement.orderId === currentValue.orderId &&
        lastElement.gameNo === currentValue.gameNo
      ) {
        lastElement.packs.push(currentValue.packNo);
        lastElement.gamePacksValue += currentValue.gamePacksValue;
      } else {
        currentValue.packs = [currentValue.packNo];
        previousValue.push(currentValue);
      }

      return previousValue;
    }, []);
  }

  fillAlertContent(results) {
    this.content = {
      gridContent: {},
      ...dashboardHelpers.getAlertDetails(
        this.config,
        this.gettextCatalog,
        this.data.type,
        this.data.totalCount,
        this.retailerId,
        this.session
      ),
      type: this.data.type,
    };
    this.content.gridContent = results;
  }

  onDownloadAlertReport() {
    return api.downloadAlertReport(this.retailerId, this.data.alertId);
  }

  triggerDownload({ data }) {
    return Promise.resolve();
  }

  onDismissAlert() {
    api
      .dismissAlert(this.retailerId, this.data.alertId)
      .then(() => this.modal.close())
      .then(() => this.refreshData())
      .catch(() => this.refreshData());
  }
}

export default class AlertComponent {
  constructor() {
    this.bindings = {
      data: '<',
      isStoreProfile: '<',
      refreshData: '&',
      returnGames: '&',
      showGameInventoryButtons: '<',
    };
    this.template = alertTemplate;
    this.controller = AlertController;
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/dashboard/components/dashboard-widget/alerts/alert/alert-component.js